import { useState, useRef, useEffect } from "react"
import {
  Search,
  Filter,
  MessageSquare,
  ThumbsUp,
  Share2,
  Bookmark,
  ImageIcon,
  Link2,
  FileText,
  Send,
  Clock,
  X,
  ExternalLink,
} from "lucide-react"
import CommunityBanner from "./CommunityBanner"
import useAuth from "../../hooks/useAuth"
import { useCommunity } from "../../context/CommunityContext"
import usePost from "../../hooks/usePost"
import { 
  getPostComments,
  createComment,
  likeComment as likeCommentAPI,
  unlikeComment as unlikeCommentAPI,
  deleteComment as deleteCommentAPI 
} from "../../api/posts"

const categories = ["All Posts", "Announcements", "Discussions", "Questions", "Resources", "Events"]

const CreatePostBox = ({ communityId, onSuccess }) => {
  const { user } = useAuth();
  const { createPost } = usePost();

  const [content, setContent] = useState('');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const mediaInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = process.env.REACT_APP_ALLOWED_FILE_TYPES?.split(',') || [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/ogg',
    ];
    const maxSize = parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10) || 10 * 1024 * 1024;

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`Unsupported file: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        setError(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return false;
      }
      return true;
    });

    setSelectedMedia(prev => [...prev, ...validFiles]);
    setMediaPreview(prev => [
      ...prev,
      ...validFiles.map(file => ({
        file,
        url: URL.createObjectURL(file),
        type: file.type.startsWith('image/') ? 'image'
             : file.type.startsWith('video/') ? 'video'
             : 'document'
      }))
    ]);

    e.target.value = null;
  };

  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = [
      'application/pdf', 'application/msword', 'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    const maxSize = parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10) || 10 * 1024 * 1024;

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`Unsupported document type: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        setError(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return false;
      }
      return true;
    });

    setSelectedMedia(prev => [...prev, ...validFiles]);
    setMediaPreview(prev => [
      ...prev,
      ...validFiles.map(file => ({
        file,
        url: URL.createObjectURL(file),
        type: 'document',
        name: file.name
      }))
    ]);

    e.target.value = null;
  };

  const handleAddLink = () => {
    if (!linkUrl.trim()) {
      setError("Please enter a valid URL");
      return;
    }

    let formattedUrl = linkUrl;
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    // Add the link to the content
    setContent(prev => {
      const newContent = prev ? `${prev}\n\n${formattedUrl}` : formattedUrl;
      return newContent;
    });

    // Reset link UI
    setLinkUrl('');
    setShowLinkInput(false);
  };

  const handleRemoveMedia = (index) => {
    if (mediaPreview[index]?.url) URL.revokeObjectURL(mediaPreview[index].url);

    const newMedia = [...selectedMedia];
    const newPreview = [...mediaPreview];
    newMedia.splice(index, 1);
    newPreview.splice(index, 1);

    setSelectedMedia(newMedia);
    setMediaPreview(newPreview);
  };

  const handleSubmit = async () => {
    if (!content.trim() && selectedMedia.length === 0) {
      setError("Write something or add media.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const formData = new FormData();
      formData.append('content', content.trim());
      formData.append('visibility', 'public');

      if (communityId) formData.append('communityId', communityId);
      selectedMedia.forEach(file => formData.append('media', file));

      await createPost(formData, progress => {
        console.log(`Upload: ${Math.round((progress.loaded / progress.total) * 100)}%`);
      });

      setContent('');
      setSelectedMedia([]);
      setMediaPreview([]);
      setShowLinkInput(false);
      setLinkUrl('');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || 'Post failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex gap-4">
        <img src={user?.profilePicture || "/placeholder.svg?height=40&width=40"} alt="User avatar" className="w-10 h-10 rounded-full" />
        <div className="flex-1">
          <textarea
            placeholder="Start a discussion..."
            value={content}
            onChange={handleContentChange}
            rows={3}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />

          {/* Link Input */}
          {showLinkInput && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL (https://example.com)"
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleAddLink}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => setShowLinkInput(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {mediaPreview.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {mediaPreview.map((item, idx) => (
                <div key={idx} className="relative">
                  <button
                    onClick={() => handleRemoveMedia(idx)}
                    className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow z-10"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                  {item.type === 'image' ? (
                    <img src={item.url} alt="preview" className="rounded-md w-full h-32 object-cover" />
                  ) : item.type === 'video' ? (
                    <video src={item.url} controls className="w-full h-32 rounded-md object-contain" />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 rounded flex flex-col items-center justify-center text-sm text-gray-500 p-2">
                      <FileText className="w-8 h-8 mb-2" />
                      <span className="truncate w-full text-center">{item.name || 'Document'}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => mediaInputRef.current?.click()}
                type="button"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Add image or video"
              >
                <ImageIcon className="w-5 h-5 text-gray-500" />
              </button>
              <input
                type="file"
                ref={mediaInputRef}
                multiple
                accept="image/*,video/*"
                hidden
                onChange={handleMediaSelect}
              />
              <button 
                onClick={() => documentInputRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Add document"
              >
                <FileText className="w-5 h-5 text-gray-500" />
              </button>
              <input
                type="file"
                ref={documentInputRef}
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                hidden
                onChange={handleDocumentSelect}
              />
              <button 
                onClick={() => setShowLinkInput(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Add link"
              >
                <Link2 className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>

          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
};

const PostMedia = ({ media }) => {
  if (!media || media.length === 0) return null;

  // Display the first media item
  const firstItem = media[0];

  return (
    <div className="border-t border-b border-gray-200">
      {firstItem.type === 'image' ? (
        <div className="flex justify-center bg-gray-100 p-2">
          <img
            src={firstItem.url}
            alt="Post media"
            className="max-h-[500px] object-contain"
            style={{ maxWidth: '100%' }}
          />
        </div>
      ) : firstItem.type === 'video' ? (
        <div className="flex justify-center bg-gray-100 p-2">
          <video
            src={firstItem.url}
            controls
            className="max-h-[500px]"
            style={{ maxWidth: '100%' }}
          />
        </div>
      ) : (
        <div className="p-4 flex items-center justify-center">
          <a
            href={firstItem.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
          >
            <FileText className="w-6 h-6" />
            <span>{firstItem.name || 'Download document'}</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      )}
      
      {media.length > 1 && (
        <div className="p-2 flex items-center gap-2 border-t border-gray-200 bg-gray-50">
          <span className="text-sm text-gray-500 font-medium">+{media.length - 1} more</span>
          <div className="flex">
            {media.slice(1, 4).map((item, index) => (
              <div
                key={index}
                className="w-8 h-8 -ml-2 first:ml-0 rounded-md overflow-hidden border border-white"
              >
                {item.type === 'image' ? (
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                ) : item.type === 'video' ? (
                  <div className="bg-gray-200 w-full h-full flex items-center justify-center">
                    <span className="text-xs">Vid</span>
                  </div>
                ) : (
                  <div className="bg-gray-200 w-full h-full flex items-center justify-center">
                    <span className="text-xs">Doc</span>
                  </div>
                )}
              </div>
            ))}
            {media.length > 4 && (
              <div className="w-8 h-8 -ml-2 rounded-md overflow-hidden border border-white bg-gray-200 flex items-center justify-center">
                <span className="text-xs">+{media.length - 4}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CommentItem = ({ comment, postId, onDelete, authUser }) => {
  const [isLiked, setIsLiked] = useState(comment.likes?.includes(authUser?._id));
  const [likesCount, setLikesCount] = useState(comment.likes?.length || 0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleLikeToggle = async () => {
    if (!authUser) return;
    
    try {
      if (isLiked) {
        await unlikeCommentAPI(comment._id);
        setLikesCount(likesCount - 1);
      } else {
        await likeCommentAPI(comment._id);
        setLikesCount(likesCount + 1);
      }
      setIsLiked(!isLiked);
    } catch (err) {
      console.error("Error toggling comment like:", err);
    }
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        setIsDeleting(true);
        await deleteCommentAPI(comment._id);
        onDelete(comment._id);
      } catch (err) {
        console.error("Error deleting comment:", err);
        alert("Failed to delete comment");
      } finally {
        setIsDeleting(false);
      }
    }
  };
  
  // Check if user owns the comment or is the post owner/admin
  const canDelete = authUser && (comment.user._id === authUser._id);
  
  return (
    <div className="flex gap-3 py-3">
      <img
        src={comment.user?.profilePicture || "/placeholder.svg?height=32&width=32"}
        alt={`${comment.user?.name || 'User'} avatar`}
        className="w-8 h-8 rounded-full"
      />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium">{comment.user?.name || 'Anonymous'}</h4>
          <span className="text-xs text-gray-500">
            {new Date(comment.createdAt).toLocaleString()}
            {comment.isEdited && <span className="ml-1">(edited)</span>}
          </span>
        </div>
        <p className="text-gray-600 text-sm whitespace-pre-wrap">{comment.content}</p>
        
        {/* Comment Media */}
        {comment.media && (
          <div className="mt-2">
            {comment.media.type === 'image' ? (
              <img 
                src={comment.media.url} 
                alt="Comment attachment" 
                className="max-h-40 rounded object-contain" 
              />
            ) : comment.media.type === 'video' && (
              <video 
                src={comment.media.url} 
                controls 
                className="max-h-40 rounded" 
              />
            )}
          </div>
        )}
        
        <div className="flex items-center gap-4 mt-1">
          <button 
            onClick={handleLikeToggle}
            disabled={!authUser}
            className={`text-xs ${isLiked ? 'text-indigo-600' : 'text-gray-500'} hover:text-indigo-600 flex items-center gap-1`}
          >
            <ThumbsUp className={`w-3 h-3 ${isLiked ? 'fill-indigo-600' : ''}`} />
            {likesCount > 0 && likesCount}
            {isLiked ? 'Liked' : 'Like'}
          </button>
          
          {canDelete && (
            <button 
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-xs text-gray-500 hover:text-red-600"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export function CommunityPostsPage() {
  const communityCtx = useCommunity();
  const { 
    fetchCommunityPosts, 
    communityPosts, 
    loading, 
    error, 
    likePost,
    unlikePost,
    commentCounts,
    loadingComments, 
    fetchAllCommentCounts,
    updateCommentCount
  } = usePost();
  const authCtx = useAuth();

  const [selectedCategory, setSelectedCategory] = useState("All Posts")
  const [sortBy, setSortBy] = useState("recent")
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPosts, setFilteredPosts] = useState([])
  const [expandedComments, setExpandedComments] = useState([])
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [commentInputs, setCommentInputs] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [commentErrors, setCommentErrors] = useState({});
  const [postComments, setPostComments] = useState({});
  const [commentFetching, setCommentFetching] = useState({});
  const commentInputRefs = useRef({})
  
  // Fetch community posts
  useEffect(() => {
    if (communityCtx.community?.community?._id) {
      fetchCommunityPosts(communityCtx.community.community._id, { page, limit });
    }
  }, [communityCtx.community, fetchCommunityPosts, page, limit]);
  
  // Fetch accurate comment counts when posts load
  useEffect(() => {
    if (!loading && communityPosts && communityPosts.length > 0) {
      fetchAllCommentCounts();
    }
  }, [loading, communityPosts, fetchAllCommentCounts]);
  
  // Filter posts effect - keep as is
  useEffect(() => {
    if (!communityPosts) {
      setFilteredPosts([]);
      return;
    }

    let result = [...communityPosts];

    // Apply category filter if not "All Posts"
    if (selectedCategory !== "All Posts") {
      // This would work if your posts have a category field
      // result = result.filter(post => post.category === selectedCategory);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (post) =>
          post.content?.toLowerCase().includes(query) ||
          post.user?.name?.toLowerCase().includes(query) ||
          post.user?.username?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case "recent":
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "popular":
        result.sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
        break;
      case "discussed":
        result.sort((a, b) => (b.commentsCount || 0) - (a.commentsCount || 0));
        break;
      default:
        break;
    }

    setFilteredPosts(result);
  }, [communityPosts, searchQuery, sortBy, selectedCategory]);

  // Fetch comments for a post - update to ensure the comment count matches reality
  const fetchComments = async (postId) => {
    if (commentFetching[postId]) return; // Prevent multiple fetches
    
    try {
      setCommentFetching(prev => ({ ...prev, [postId]: true }));
      const response = await getPostComments(postId, { page: 1, limit: 20, parentOnly: true });
      
      if (response?.data?.data?.comments) {
        const fetchedComments = response.data.data.comments;
        setPostComments(prev => ({
          ...prev,
          [postId]: fetchedComments
        }));
        
        // Update accurate comment count in our central state
        const actualCount = response.data.pagination?.total || fetchedComments.length;
        updateCommentCount(postId, actualCount);
      } else {
        console.error("Invalid comment data structure:", response);
        setCommentErrors(prev => ({
          ...prev, 
          [postId]: "Failed to load comments - invalid data structure"
        }));
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      setCommentErrors(prev => ({
        ...prev,
        [postId]: "Failed to load comments"
      }));
    } finally {
      setCommentFetching(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Toggle comment section expansion
  const toggleComments = (postId) => {
    const isCurrentlyExpanded = expandedComments.includes(postId);
    
    if (!isCurrentlyExpanded && !postComments[postId]) {
      // Only fetch comments if we're expanding and don't have them yet
      fetchComments(postId);
    }
    
    setExpandedComments(prev => 
      isCurrentlyExpanded 
        ? prev.filter(id => id !== postId) 
        : [...prev, postId]
    );
  }

  // Focus comment input when clicking on comment button
  const focusCommentInput = (postId) => {
    if (!expandedComments.includes(postId)) {
      toggleComments(postId);
    }
    
    setTimeout(() => {
      commentInputRefs.current[postId]?.focus();
    }, 100);
  }

  // Handle comment input change
  const handleCommentChange = (postId, value) => {
    setCommentInputs(prev => ({
      ...prev,
      [postId]: value
    }));
  };

  // Handle comment submission
  const handleCommentSubmit = async (postId) => {
    const commentContent = commentInputs[postId];
    if (!commentContent?.trim()) return;

    try {
      setCommentLoading(prev => ({ ...prev, [postId]: true }));
      setCommentErrors(prev => ({ ...prev, [postId]: null }));

      const response = await createComment({
        postId: postId,
        content: commentContent.trim()
      });
      
      if (response?.data?.data?.comment) {
        const newComment = response.data.data.comment;
        
        // Add to existing comments list
        setPostComments(prev => {
          const currentComments = prev[postId] || [];
          return { ...prev, [postId]: [newComment, ...currentComments] };
        });
        
        // Update comment count state (increment by 1)
        updateCommentCount(
          postId, 
          (commentCounts[postId] || 0) + 1
        );
        
        // Clear input
        setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      } else {
        console.error("Invalid comment response:", response);
        setCommentErrors(prev => ({
          ...prev, 
          [postId]: "Unexpected server response"
        }));
      }
    } catch (err) {
      console.error("Error submitting comment:", err);
      setCommentErrors(prev => ({
        ...prev,
        [postId]: err?.response?.data?.message || "Failed to submit comment"
      }));
    } finally {
      setCommentLoading(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Handle comment deletion
  const handleCommentDeleted = (postId, commentId) => {
    // Remove from comments list
    setPostComments(prev => {
      if (!prev[postId]) return prev;
      const updatedComments = prev[postId].filter(comment => comment._id !== commentId);
      return { ...prev, [postId]: updatedComments };
    });
    
    // Update comment count (decrement by 1)
    updateCommentCount(
      postId, 
      Math.max((commentCounts[postId] || 0) - 1, 0)
    );
  };

  // Handle like/unlike post
  const handleToggleLike = async (post) => {
    if (!authCtx.isAuth) {
      // Handle unauthenticated state - maybe show a login prompt
      return;
    }

    try {
      const isLiked = post.likes?.includes(authCtx.user?._id);
      
      if (isLiked) {
        await unlikePost(post._id);
      } else {
        await likePost(post._id);
      }
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  // Handle save post
  const handleSavePost = async (postId) => {
    // This would need to be implemented in your usePost hook
    // try {
    //   const isSaved = userSavedPosts.includes(postId);
    //   if (isSaved) {
    //     await unsavePost(postId);
    //   } else {
    //     await savePost(postId);
    //   }
    // } catch (err) {
    //   console.error("Error saving post:", err);
    // }
  };

  // Refresh posts after creating a new one
  const handlePostSuccess = () => {
    if (communityCtx.community?.community?._id) {
      fetchCommunityPosts(communityCtx.community.community._id, { page: 1, limit });
      setPage(1);
    }
  };

  const timeAgoFormatter = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const postDate = new Date(date);
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return postDate.toLocaleDateString();
  };

  // Add this function to handle loading more comments
  const fetchMoreComments = async (postId) => {
    if (commentFetching[postId]) return;
    
    try {
      setCommentFetching(prev => ({ ...prev, [postId]: true }));
      
      // Calculate the next page based on how many comments we already have
      const currentComments = postComments[postId] || [];
      const nextPage = Math.floor(currentComments.length / 20) + 1;
      
      const response = await getPostComments(postId, { 
        page: nextPage, 
        limit: 20, 
        parentOnly: true 
      });
      
      if (response?.data?.data?.comments) {
        const newComments = response.data.data.comments;
        
        // Append new comments to existing ones
        setPostComments(prev => {
          const currentComments = prev[postId] || [];
          return {
            ...prev,
            [postId]: [...currentComments, ...newComments]
          };
        });
      } else {
        console.error("Invalid response format when loading more comments:", response);
        setCommentErrors(prev => ({
          ...prev,
          [postId]: "Failed to load more comments - invalid data structure"
        }));
      }
    } catch (err) {
      console.error("Error fetching more comments:", err);
      setCommentErrors(prev => ({
        ...prev,
        [postId]: "Failed to load more comments"
      }));
    } finally {
      setCommentFetching(prev => ({ ...prev, [postId]: false }));
    }
  };

  return (
    <div className="space-y-6 max-w-[2000px] mx-auto">
      <CommunityBanner
        title={communityCtx.community.name}
        description={communityCtx.community.description}
      />

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="search"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="recent">Most Recent</option>
            <option value="popular">Most Popular</option>
            <option value="discussed">Most Discussed</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`
              flex items-center px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors
              ${
                selectedCategory === category
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }
            `}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Create Post Box */}
      {communityCtx.community?.community?._id && (
        <CreatePostBox 
          communityId={communityCtx.community.community._id} 
          onSuccess={handlePostSuccess} 
        />
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="flex justify-center p-6">
          <div className="loader">Loading...</div>
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p>Error loading posts: {error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredPosts.length === 0 && (
        <div className="bg-white rounded-xl p-10 text-center shadow-sm">
          <h3 className="text-lg font-semibold mb-2">No posts yet</h3>
          <p className="text-gray-500 mb-6">Be the first to post in this community!</p>
          <button 
            onClick={() => document.querySelector('textarea')?.focus()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Create a post
          </button>
        </div>
      )}

      {/* Posts Feed */}
      {!loading && filteredPosts.length > 0 && (
        <div className="space-y-6">
          {filteredPosts.map((post) => {
            const isLiked = post.likes?.includes(authCtx.user?._id);
            const isCommentsExpanded = expandedComments.includes(post._id);
            const comments = postComments[post._id] || [];
            const isLoadingComments = commentFetching[post._id] || false;
            const commentError = commentErrors[post._id];
            
            // Use only our accurate comment count, with proper loading state
            const commentCount = loadingComments ? null : (commentCounts[post._id] || 0);
            
            return (
              <article key={post._id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                {/* Post Header */}
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <img
                      src={post.user?.profilePicture || "/placeholder.svg?height=40&width=40"}
                      alt={`${post.user?.name || 'User'} avatar`}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{post.user?.name || 'Anonymous User'}</h3>
                            {post.user?.role && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-600">
                                {post.user.role}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {timeAgoFormatter(post.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-gray-600 whitespace-pre-wrap">
                    {post.content}
                    
                    {/* Render URLs as clickable links */}
                    {post.content && post.content.match(/https?:\/\/[^\s]+/g) && (
                      <div className="mt-2">
                        {post.content.match(/https?:\/\/[^\s]+/g).map((url, index) => (
                          <a 
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline block truncate"
                          >
                            {url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Post Media */}
                <PostMedia media={post.media} />

                {/* Post Actions */}
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <button 
                        className={`flex items-center gap-2 ${isLiked ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'} transition-colors`}
                        onClick={() => handleToggleLike(post)}
                      >
                        <ThumbsUp className={`w-5 h-5 ${isLiked ? 'fill-indigo-600' : ''}`} />
                        <span>{post.likes?.length || 0}</span>
                      </button>
                      <button
                        className="flex items-center gap-2 text-gray-500 hover:text-indigo-600 transition-colors"
                        onClick={() => focusCommentInput(post._id)}
                      >
                        <MessageSquare className="w-5 h-5" />
                        {loadingComments ? (
                          <span className="inline-block w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          <span>{commentCount}</span>
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => handleSavePost(post._id)}
                      >
                        <Bookmark className="w-5 h-5" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <Share2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div
                    className={`mt-4 pt-4 border-t border-gray-200 ${isCommentsExpanded ? "block" : "hidden"}`}
                  >
                    {/* Comment Input */}
                    {authCtx.isAuth && (
                      <div className="mb-4 flex gap-4">
                        <img 
                          src={authCtx.user?.profilePicture || "/placeholder.svg?height=32&width=32"} 
                          alt="User avatar" 
                          className="w-8 h-8 rounded-full" 
                        />
                        <div className="flex-1 relative">
                          <input
                            ref={(el) => {
                              if (el) commentInputRefs.current[post._id] = el
                            }}
                            type="text"
                            placeholder="Write a comment..."
                            value={commentInputs[post._id] || ''}
                            onChange={(e) => handleCommentChange(post._id, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleCommentSubmit(post._id);
                            }}
                            className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={commentLoading[post._id]}
                          />
                          <button 
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200"
                            onClick={() => handleCommentSubmit(post._id)}
                            disabled={commentLoading[post._id] || !commentInputs[post._id]?.trim()}
                          >
                            <Send className={`w-4 h-4 ${commentLoading[post._id] ? 'text-gray-400' : 'text-indigo-600'}`} />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Error message */}
                    {commentError && (
                      <div className="mb-4 p-2 bg-red-50 text-red-600 text-sm rounded">
                        {commentError}
                      </div>
                    )}
                    
                    {/* Comments List */}
                    <div className="space-y-1 divide-y divide-gray-100">
                      {isLoadingComments ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                          <p className="mt-2 text-sm text-gray-500">Loading comments...</p>
                        </div>
                      ) : comments.length > 0 ? (
                        <>
                          <div className="py-2 text-sm text-gray-500">
                            Showing {comments.length} of {commentCount} comments
                          </div>
                          {comments.map((comment) => (
                            <CommentItem 
                              key={comment._id} 
                              comment={comment} 
                              postId={post._id}
                              authUser={authCtx.user}
                              onDelete={(commentId) => handleCommentDeleted(post._id, commentId)}
                            />
                          ))}
                          {comments.length < commentCount && (
                            <button 
                              onClick={() => fetchMoreComments(post._id)}
                              className="w-full py-2 text-sm text-center text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              disabled={commentFetching[post._id]}
                            >
                              {commentFetching[post._id] ? 'Loading...' : 'Show more comments'}
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="text-center text-gray-500 py-4">
                          No comments yet. Be the first to comment!
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Show/Hide Comments Button */}
                  <button
                    onClick={() => toggleComments(post._id)}
                    className="mt-4 w-full py-2 text-sm text-center text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    {isCommentsExpanded ? 'Hide comments' : loadingComments ? (
                      <span className="flex items-center justify-center">
                        <span className="inline-block w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mr-2"></span>
                        Loading comments...
                      </span>
                    ) : (
                      `Show ${commentCount} comments`
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  )
}

